"use strict";

/**
 * Recursively traverse a data structure and replace any `*_html` helper fields
 * with their corresponding base fields. This enables external HTML content to
 * override inline content in form configurations.
 *
 * @param {*} node - Arbitrary JSON-compatible structure to process.
 * @param {Object} [options] - Configuration options.
 * @param {string} [options.baseUrl] - Base URL used to resolve relative paths when fetching external files.
 * @param {Object} [options.assets] - Pre-bundled HTML content map (path -> content string).
 * @param {typeof fetch} [options.fetchFn=fetch] - Custom fetch implementation (mainly for testing).
 *
 * @returns {Promise<*>} A deep-cloned structure with HTML overrides applied.
 *
 * @example
 * // Input object
 * const config = {
 *   body: "Default text",
 *   body_html: "./html/content.html"
 * };
 *
 * // After processing, body is replaced with HTML file content
 * const resolved = await resolveHtmlFields(config, {
 *   baseUrl: "https://example.com/docs/",
 *   assets: { "./html/content.html": "<p>HTML content</p>" }
 * });
 * // Result: { body: "<p>HTML content</p>", body_html: "./html/content.html" }
 */
export async function resolveHtmlFields(
    node,
    { baseUrl, assets, fetchFn = fetch } = {}
) {
    // Handle arrays by recursively processing each element
    if (Array.isArray(node)) {
        const result = [];
        for (const item of node) {
            // Recursively process each array item with the same options
            result.push(
                await resolveHtmlFields(item, { baseUrl, assets, fetchFn })
            );
        }
        return result;
    }

    //  Handle objects (the main processing logic)
    if (node && typeof node === "object") {
        // Create a deep clone to avoid mutating the original object
        const clone = {};
        const entries = Object.entries(node);

        // First pass - recursively process all nested values
        // This ensures deeply nested structures are processed before we handle HTML fields
        for (const [key, value] of entries) {
            clone[key] = await resolveHtmlFields(value, {
                baseUrl,
                assets,
                fetchFn,
            });
        }

        // Second pass - look for HTML partial fields and resolve them
        for (const [key, value] of entries) {
            // Check if this field is an HTML partial reference:
            // 1. Must be a non-empty string
            // 2. Must end with "_html" (case insensitive)
            if (
                typeof value === "string" &&
                value.trim().length > 0 &&
                key.toLowerCase().endsWith("_html")
            ) {
                // Calculate the target field name by removing "_html" suffix
                // Example: "body_html" -> "body"
                const targetKey = key.replace(/_html$/i, "");
                let html = value; // Default to the original value

                // Normalize the path to ensure it starts with "./"
                // This creates consistent keys for asset lookup
                const normalized = value.startsWith("./")
                    ? value
                    : `./${value}`;

                // Try to get HTML content from pre-bundled assets first
                if (
                    assets &&
                    Object.prototype.hasOwnProperty.call(assets, normalized)
                ) {
                    const assetRef = assets[normalized];

                    // Check if the asset reference is already HTML content
                    // (contains HTML-like characters: <, >, &)
                    if (
                        typeof assetRef === "string" &&
                        /[<>&]/.test(assetRef)
                    ) {
                        // Asset contains HTML content directly - use it as-is
                        html = assetRef;
                    } else {
                        try {
                            // Asset reference is a URL/path - fetch the content
                            // Build absolute URL using baseUrl or current page location
                            const url = assetRef.startsWith("http")
                                ? assetRef // Already absolute URL
                                : new URL(
                                      assetRef,
                                      baseUrl || window.location.href
                                  ).href;

                            // Fetch the HTML content from the URL
                            const response = await fetchFn(url);
                            if (!response.ok) {
                                console.warn(
                                    `Failed to load HTML fragment at ${url} (${response.status}), using fallback content`
                                );
                                // Keep original value as fallback - don't throw
                            } else {
                                html = await response.text();
                            }
                        } catch (error) {
                            console.warn(
                                `Error loading HTML fragment from assets: ${error.message}, using fallback content`
                            );
                            // Keep original value as fallback - don't throw
                        }
                    }
                }
                // If no pre-bundled asset, try fetching from URL
                else if (!/[<>&]/.test(value) && baseUrl) {
                    try {
                        // Value doesn't look like HTML content and we have a baseUrl
                        // Treat it as a relative path and fetch the content
                        const url = new URL(value, baseUrl).href;
                        const response = await fetchFn(url);
                        if (!response.ok) {
                            console.warn(
                                `Failed to load HTML fragment at ${url} (${response.status}), using fallback content`
                            );
                            // Keep original value as fallback - don't throw
                        } else {
                            html = await response.text();
                        }
                    } catch (error) {
                        console.warn(
                            `Error loading HTML fragment from URL: ${error.message}, using fallback content`
                        );
                        // Keep original value as fallback - don't throw
                    }
                }
                // If neither condition is met, html remains the original value

                // Override the target field with resolved HTML content
                // This replaces the base field (e.g., "body") with HTML content
                // while keeping the original HTML field (e.g., "body_html") for reference
                clone[targetKey] = html;
            }
        }

        return clone;
    }

    // Handle primitive values (strings, numbers, booleans, null)
    // Return as-is since they don't need processing
    return node;
}
